// Context menu for folder items: rename, export, delete.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getActiveZoom } from '../../lib/zoomPosition';

interface FolderMenuProps {
  folderId: string;
  folderName: string;
  chatCount: number;
  anchorRect: DOMRect;
  onRename: () => void;
  onExportFolder: () => void;
  onDelete: () => void;
  onClose: () => void;
}

export default function FolderMenu({
  folderName,
  chatCount,
  anchorRect,
  onRename,
  onExportFolder,
  onDelete,
  onClose,
}: FolderMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const z = getActiveZoom();
  const top = anchorRect.bottom / z + 4;
  const left = Math.min(anchorRect.left / z, window.innerWidth / z - 200);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (confirmDelete) {
          setConfirmDelete(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, confirmDelete]);

  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="malpedia-context-menu"
      style={{ top, left }}
      role="menu"
    >
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onRename(); onClose(); }}
      >
        Rename
      </button>
      <button
        className="malpedia-context-menu-item"
        role="menuitem"
        onClick={() => { onExportFolder(); onClose(); }}
      >
        Export folder
      </button>

      <div className="malpedia-context-menu-divider" />

      {confirmDelete ? (
        <div className="malpedia-context-menu-confirm">
          <span className="malpedia-context-menu-confirm-text">
            Move {chatCount} chat{chatCount !== 1 ? 's' : ''} to Unfiled and delete?
          </span>
          <div className="malpedia-context-menu-confirm-actions">
            <button
              className="malpedia-context-menu-item malpedia-context-menu-item--danger"
              role="menuitem"
              onClick={() => { onDelete(); onClose(); }}
            >
              Delete
            </button>
            <button
              className="malpedia-context-menu-item"
              role="menuitem"
              onClick={() => setConfirmDelete(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          className="malpedia-context-menu-item malpedia-context-menu-item--danger"
          role="menuitem"
          onClick={() => {
            if (chatCount > 0) {
              setConfirmDelete(true);
            } else {
              onDelete();
              onClose();
            }
          }}
        >
          Delete folder
        </button>
      )}
    </div>,
    document.body,
  );
}
